import { postAPI } from '~/lib/api';
import { NestedAutocompleteAddress } from '@habity/types';
import { isDev } from '~/lib/utils';

/**
 * Parse address string to extract components for residential registry search
 * Handles Swedish address formats:
 * - "Storgatan 1" -> name: "Storgatan", number: "1"
 * - "Storgatan 1A" -> name: "Storgatan", number: "1", suffix: "A"
 * - "Storgatan 1-3" -> name: "Storgatan", number: "1"
 * - "Storgatan 1 A" -> name: "Storgatan", number: "1", suffix: "A"
 */
export function parseAddressForRegistry(address: NestedAutocompleteAddress) {
  const street = address.street || '';
  const city = address.city || '';
  const postalCode = address.zip || '';
  const apartmentNumber = address.apartment_number;

  // Extract street name and number from the full street string
  // Regex explanation:
  // ^(.+?) - Street name (non-greedy)
  // \s+ - One or more spaces
  // (\d+) - Street number (one or more digits)
  // (?:-\d+)? - Optional range (e.g., "-3" in "1-3")
  // \s*([A-ZÅÄÖ])? - Optional suffix letter (with optional space before it)
  // $ - End of string
  const streetMatch = street.match(/^(.+?)\s+(\d+)(?:-\d+)?\s*([A-ZÅÄÖ])?$/i);
  
  let streetName = street;
  let streetNumber: string | undefined;
  let streetSuffix: string | undefined;

  if (streetMatch) {
    streetName = streetMatch[1].trim();
    streetNumber = streetMatch[2];
    streetSuffix = streetMatch[3]?.toUpperCase();
  }

  return {
    street_name: streetName,
    street_number: streetNumber,
    street_suffix: streetSuffix,
    postal_code: postalCode,
    postal_city: city,
    unit_number: apartmentNumber?.toString(),
    limit: 1,
  };
}

/**
 * Enrich address data with information from RealAI residential registry
 */
export async function enrichAddressWithRegistry(
  address: NestedAutocompleteAddress
): Promise<NestedAutocompleteAddress> {
  try {
    const searchParams = parseAddressForRegistry(address);
    if (isDev) {
      console.log('🔍 Residential Registry Search:', {
        input: address.street,
        searchParams,
      });
    }

    // Call the residential registry API with address_id to update the database
    const data = await postAPI(
      '/providers/realai/residential-registry',
      JSON.stringify({
        ...searchParams,
        address_id: address.id, // Pass address_id to update addresses_autocomplete table
      })
    );


    if (!data) {
      if (isDev) {
        console.log('❌ No residential registry data found for address.');
      }
      return address;
    }

    if (isDev) {
      console.log('✅ Residential Registry Result:', data);
    }

    // Merge registry data with existing address data
    // Only update fields that are missing or could be more accurate
    // Note: ownership field will be available after migration is applied and types regenerated
    const enrichedAddress = {
      ...address,
      // Update living space if available and different
      living_space: data.living_area ?? address.living_space,
      // Update rooms if available
      rooms: data.rooms_total ?? address.rooms,
      // Update year built if available
      build_year: data.year_built ?? address.build_year,
      // Update coordinates if more accurate
      latitude: data.latitude ?? address.latitude,
      longitude: data.longitude ?? address.longitude,
      // Update ownership type if available
      ownership: data.occupant_type ?? address.ownership,
    } as NestedAutocompleteAddress;

    if (isDev) {
      console.log('✨ Address enriched with residential registry data:', {
        original: {
          living_space: address.living_space,
          rooms: address.rooms,
          build_year: address.build_year,
        },
        enriched: {
          living_space: enrichedAddress.living_space,
          rooms: enrichedAddress.rooms,
          build_year: enrichedAddress.build_year,
        },
        // universal_property_id: data.universal_property_id,
      });
    }

    return enrichedAddress;
  } catch (error) {
    console.error('Error enriching address with residential registry:', error);
    // Return original address if enrichment fails
    return address;
  }
}
